<template>
    <i
        v-if="display === 'prompt'"
        class="fa fa-bell-exclamation text-white"
        v-animation="{ cls: 'fa-bounce', gap: 3 }"
        v-tooltip="`You've subscribed to notifications but your browser is not allowing push notifications. Click to enable.`"
        @click="enableNotifications"
    ></i>

    <i
        v-else-if="display === 'blocked'"
        class="fa fa-light fa-bell-slash opacity-50 text-red-400"
        v-tooltip="`You've subscribed to notifications but your browser has blocked notifications. Click here for help unblocking them.`"
        @click="showUnblockInstructions"
    ></i>
</template>

<script lang="ts" setup>
import { GlobalState } from '@/global';
import { DataCache } from '@/shared/data/cache';
import { FCM } from '@/shared/services/fcm';
import { showToast } from '@signal24/vue-foundation';
import { nextTick, ref, watch } from 'vue';

const display = ref<'none' | 'prompt' | 'blocked'>('none');

function enableNotifications() {
    FCM.registerNotifications()
        .then(didRegister => {
            if (didRegister) {
                showToast({ message: 'Notifications have been successfully enabled in your browser!', durationSecs: 10, position: 'top' });
            }
        })
        .finally(setDisplay);
}

function showUnblockInstructions() {
    if (navigator.userAgent.includes('Firefox/')) {
        window.open('https://support.mozilla.org/en-US/kb/site-permissions-panel', '_blank');
    } else if (navigator.userAgent.includes('Edg/')) {
        window.open(
            'https://support.microsoft.com/en-us/microsoft-edge/manage-website-notifications-in-microsoft-edge-0c555609-5bf2-479d-a59d-fb30a0b80b2b',
            '_blank'
        );
    } else if (navigator.userAgent.includes('Chrome/')) {
        window.open('https://support.google.com/chrome/answer/3220216?hl=en&co=GENIE.Platform=Desktop', '_blank');
    } else if (navigator.userAgent.includes('Safari/') && /Version\/1[2-9]|[2-9][0-9]/.test(navigator.userAgent)) {
        window.open('https://support.apple.com/guide/safari/manage-website-notifications-sfri40734/mac', '_blank');
    } else {
        window.open('https://google.com/search?q=enable+notifications+in+my+browser', '_blank');
    }
}

const isSubscribed = ref(false);
const talkAccessWatch = watch(
    DataCache.getRef('talkAccess'),
    talkAccess =>
        nextTick(() => {
            if (!talkAccess) return;
            talkAccessWatch.stop();
            watch(DataCache.getRef('talkSubscribedNumbers'), subscribedNumbers => {
                isSubscribed.value = !!subscribedNumbers?.some(sn => sn.browserPush);
            });
        }),
    {
        immediate: true
    }
);

watch(isSubscribed, setDisplay);
function setDisplay() {
    display.value = 'none';

    if (!isSubscribed.value) return;
    if (GlobalState.isInstall) return;
    if (!('Notification' in window)) return;

    if (Notification.permission === 'default') {
        display.value = 'prompt';
    } else if (Notification.permission === 'denied') {
        display.value = 'blocked';
    }
}
</script>
